import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import Chapter from "./Chapter";
import { GALLERY } from "../data/films";

const EASE = [0.22, 1, 0.36, 1];

export default function Gallery({ setLocked }) {
    const [index, setIndex] = useState(null);

    const close = useCallback(() => setIndex(null), []);
    const step = useCallback(
        (dir) => setIndex((i) => (i + dir + GALLERY.length) % GALLERY.length),
        []
    );

    useEffect(() => {
        if (index === null) return;
        setLocked(true);
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowRight") step(1);
            if (e.key === "ArrowLeft") step(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => {
            setLocked(false);
            window.removeEventListener("keydown", onKey);
        };
    }, [index, setLocked, close, step]);

    return (
        <section id="galeria" className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32" data-testid="gallery-section">
            <Chapter num="03" eyebrow="Registro visual y rodaje" title="Galería" />
            <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">
                {GALLERY.map((img, i) => (
                    <motion.button
                        key={img.src}
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.6, ease: EASE, delay: (i % 3) * 0.08 }}
                        onClick={() => setIndex(i)}
                        className={`group relative aspect-[4/3] overflow-hidden ${i === GALLERY.length - 1 ? "col-span-2 lg:col-span-1" : ""}`}
                        aria-label={`Ampliar fotografía: ${img.caption}`}
                        data-testid={`gallery-item-${i}`}
                    >
                        <img
                            src={img.src}
                            alt={img.alt}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                        />
                        <div className="absolute inset-0 bg-[#6B1D2F]/10 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-0" />
                        <span className="absolute bottom-3 left-3 bg-[#FAF8F5]/90 px-2.5 py-1 font-mono-jf text-[9px] uppercase tracking-[0.2em] text-[#141312] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                            {img.caption}
                        </span>
                    </motion.button>
                ))}
            </div>

            {/* Lightbox accesible */}
            <AnimatePresence>
                {index !== null && (
                    <div className="fixed inset-0 z-[90] flex items-center justify-center" data-testid="gallery-lightbox">
                        <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={close}
                            className="absolute inset-0 bg-[#141312]/90 backdrop-blur-sm"
                            aria-label="Cerrar galería"
                            data-testid="lightbox-backdrop"
                        />
                        <motion.figure
                            role="dialog"
                            aria-modal="true"
                            aria-label={GALLERY[index].caption}
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.97 }}
                            transition={{ duration: 0.4, ease: EASE }}
                            className="relative z-10 mx-4 w-full max-w-4xl"
                        >
                            <img
                                src={GALLERY[index].src}
                                alt={GALLERY[index].alt}
                                className="max-h-[76vh] w-full object-contain"
                                data-testid="lightbox-image"
                            />
                            <figcaption className="mt-4 flex items-center justify-between font-mono-jf text-[10px] uppercase tracking-[0.25em] text-[#FAF8F5]/70">
                                <span data-testid="lightbox-caption">{GALLERY[index].caption}</span>
                                <span>
                                    {String(index + 1).padStart(2, "0")} / {String(GALLERY.length).padStart(2, "0")}
                                </span>
                            </figcaption>
                            <button
                                onClick={close}
                                className="absolute -top-12 right-0 text-[#FAF8F5] transition-colors duration-300 hover:text-[#C98993]"
                                aria-label="Cerrar"
                                data-testid="lightbox-close"
                            >
                                <X size={26} />
                            </button>
                            <button
                                onClick={() => step(-1)}
                                className="absolute left-0 top-1/2 -translate-y-1/2 p-3 text-[#FAF8F5]/80 transition-colors duration-300 hover:text-[#C98993] sm:-left-14"
                                aria-label="Fotografía anterior"
                                data-testid="lightbox-prev"
                            >
                                <ChevronLeft size={30} />
                            </button>
                            <button
                                onClick={() => step(1)}
                                className="absolute right-0 top-1/2 -translate-y-1/2 p-3 text-[#FAF8F5]/80 transition-colors duration-300 hover:text-[#C98993] sm:-right-14"
                                aria-label="Fotografía siguiente"
                                data-testid="lightbox-next"
                            >
                                <ChevronRight size={30} />
                            </button>
                        </motion.figure>
                    </div>
                )}
            </AnimatePresence>
        </section>
    );
}
